import { useCallback, useState } from 'react';

import { useLazyGetPeopleQuery } from '@/features/contacts/people/api/peopleApi';
import { AutocompleteAsync } from '@web/ui';

import type { Person } from '@/features/contacts/types';

export type PersonOption = {
  id: string;
  name: string;
  companyId: string | null;
  companyName?: string | null;
};

type PersonSelectFieldProps = {
  value: PersonOption | null;
  onChange: (value: PersonOption | null) => void;
  companyId?: string | null;
  label?: string;
  placeholder?: string;
  error?: string;
  disabled?: boolean;
};

const normalizePeople = (raw: unknown): Person[] => {
  if (Array.isArray(raw)) return raw as Person[];
  if (raw && typeof raw === 'object' && Array.isArray((raw as any).results)) {
    return (raw as any).results as Person[];
  }
  return [];
};

const getHasMore = (raw: unknown): boolean => {
  if (raw && typeof raw === 'object' && 'next' in raw) {
    return Boolean((raw as any).next);
  }
  return false;
};

const getPersonName = (person: Person) =>
  `${person.first_name ?? ''} ${person.last_name ?? ''}`.trim();

export function PersonSelectField({
  value,
  onChange,
  companyId = null,
  label = 'Person',
  placeholder = 'Search people...',
  error,
  disabled,
}: PersonSelectFieldProps) {
  const [getPeople] = useLazyGetPeopleQuery();
  const [inputValue, setInputValue] = useState('');

  const loadOptions = useCallback(
    async ({ query, page }: { query: string; page: number }) => {
      const params: { page: number; query?: string } = { page };
      if (query) params.query = query;

      const response = await getPeople(params).unwrap();
      const people = normalizePeople(response.data);
      const filteredPeople = companyId
        ? people.filter((person) => person.company?.id === companyId)
        : people;

      return {
        options: filteredPeople.map((person) => ({
          id: person.id,
          name: getPersonName(person) || person.email,
          companyId: person.company?.id ?? null,
          companyName: person.company?.name ?? null,
        })),
        hasMore: getHasMore(response.data),
      };
    },
    [companyId, getPeople],
  );

  return (
    <AutocompleteAsync<PersonOption>
      value={value}
      loadOptions={loadOptions}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, current) => option.id === current.id}
      inputValue={inputValue}
      onInputChange={(_event, nextValue) => setInputValue(nextValue)}
      onChange={(_event, nextValue) => onChange(nextValue)}
      renderOption={(props, option) => <li {...props}>{option.name}</li>}
      noOptionsText={companyId ? 'No people in this company' : 'No people'}
      loadingText="Loading people..."
      label={label}
      placeholder={placeholder}
      disabled={disabled}
      textFieldProps={{
        error: Boolean(error),
        helperText: error,
      }}
    />
  );
}
